import React, { PropsWithChildren } from 'react';
import { Form as CompatibleForm } from '@ant-design/compatible';
import { DomScrollIntoViewConfig, ValidateCallback, WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import Item from './Item';
import { FormInitialValueContext } from './contexts/FormInitialValueContext';
import { FormContext } from './contexts/FormContext';
import { convertFieldError } from '../../../lib/utils/form';
import { FieldError } from '../../../types/common/form';

export interface FormProps<FormValues extends object> {
  form: WrappedFormUtils;
  initialValue?: Partial<FormValues>;
  //폼 유효성 검사 성공시 콜백
  onFinish?: (value: FormValues) => void;
  //폼 유효성 검사 실패시 콜백
  onFinishFailed?: (errors: FieldError[]) => void;
  //에러시 첫번째 에러에 스크롤 여부 or 옵션
  scrollToFirstError?: boolean | DomScrollIntoViewConfig;
}

const Form = <FormValues extends object>(props: PropsWithChildren<FormProps<FormValues>>) => {
  const { form, initialValue, onFinish, onFinishFailed, scrollToFirstError, children } = props;

  const handleFormValidate: ValidateCallback<FormValues> = async (err, values) => {
    if (err) return onFinishFailed && onFinishFailed(convertFieldError(err));

    onFinish && onFinish(values);
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (scrollToFirstError) {
      const scrollOption = scrollToFirstError === true ? { offsetBottom: 100 } : scrollToFirstError;
      form?.validateFieldsAndScroll({ scroll: scrollOption }, handleFormValidate);
    } else {
      form?.validateFields(handleFormValidate);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLFormElement> = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return (
    <FormContext.Provider value={form}>
      <FormInitialValueContext.Provider value={initialValue}>
        <CompatibleForm onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
          {children}
        </CompatibleForm>
      </FormInitialValueContext.Provider>
    </FormContext.Provider>
  );
};

Form.Item = Item;
Form.create = CompatibleForm.create;

export default Form;
